import React from "react";
import type { InvitationsState } from "./useInvitationsState";

interface WizardStepTwoProps {
  state: InvitationsState;
}

const WizardStepTwo: React.FC<WizardStepTwoProps> = ({ state }) => {
  const {
    registrationData,
    isIndividual,
    entitySubType,
    newInvite,
    formAssessment,
    handleRegistrationFieldChange,
    handleCloseForm,
    setWizardStep,
    formatABN,
    formatACN,
  } = state;

  const entityLabel = isIndividual
    ? "👤 Individual Details"
    : `🏢 ${
        entitySubType === "sole-trader"
          ? "Sole Trader"
          : entitySubType === "company"
            ? "Company"
            : entitySubType === "partnership"
              ? "Partnership"
              : entitySubType === "trust"
                ? "Trust"
                : entitySubType === "association"
                  ? "Association"
                  : "Government"
      } Details`;

  // DOB helpers
  const dobParts = registrationData.dateOfBirth
    ? registrationData.dateOfBirth.split("-")
    : ["", "", ""];
  const dobYear = dobParts[0] || "";
  const dobMonth = dobParts[1] ? String(parseInt(dobParts[1], 10)) : "";
  const dobDay = dobParts[2] ? String(parseInt(dobParts[2], 10)) : "";
  const buildDate = (y: string, m: string, d: string) => {
    if (y && m && d) return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    return `${y || "0000"}-${m ? m.padStart(2, "0") : "00"}-${d ? d.padStart(2, "0") : "00"}`;
  };

  const canProceed =
    !!registrationData.firstName &&
    !!registrationData.lastName &&
    !!registrationData.email &&
    !!registrationData.phone &&
    registrationData.phone.startsWith("+");

  return (
    <div>
      <h3
        style={{
          marginBottom: 4,
          fontSize: 16,
          color: "#111",
          fontWeight: 600,
        }}
      >
        {entityLabel}
      </h3>
      {!isIndividual && newInvite.organisationName && (
        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
          Registering a new contact for <strong>{newInvite.organisationName}</strong>
        </p>
      )}

      {/* Name fields */}
      <div className="name-fields-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            value={registrationData.firstName}
            onChange={(e) =>
              handleRegistrationFieldChange("firstName", e.target.value)
            }
            placeholder="JOHN"
            required
            className="form-input"
            style={{ textTransform: "uppercase" }}
          />
          {registrationData.firstName.includes(" ") && (
            <div style={{ marginTop: 6, padding: "6px 10px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 11, color: "#92400E", lineHeight: 1.4 }}>
              ⚠ First name contains a space. If &quot;{registrationData.firstName.split(/\s+/).slice(1).join(" ")}&quot; is a middle name, please enter it in the Middle Name field.
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Middle Name{" "}
            <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 500, fontStyle: "italic" }}>
              (optional)
            </span>
          </label>
          <input
            type="text"
            value={registrationData.middleName}
            onChange={(e) =>
              handleRegistrationFieldChange("middleName", e.target.value)
            }
            placeholder="WILLIAM"
            className="form-input"
            style={{ textTransform: "uppercase" }}
          />
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            value={registrationData.lastName}
            onChange={(e) =>
              handleRegistrationFieldChange("lastName", e.target.value)
            }
            placeholder="SMITH"
            required
            className="form-input"
            style={{ textTransform: "uppercase" }}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Email *</label>
          <input
            type="email"
            value={registrationData.email}
            onChange={(e) =>
              handleRegistrationFieldChange("email", e.target.value)
            }
            placeholder="john.smith@example.com"
            required
            className="form-input"
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Phone *</label>
          <input
            type="tel"
            value={registrationData.phone}
            onChange={(e) =>
              handleRegistrationFieldChange("phone", e.target.value)
            }
            placeholder="+61 412 345 678"
            required
            className="form-input"
            style={{
              borderColor: registrationData.phone && !registrationData.phone.startsWith("+") ? "#ef4444" : undefined,
            }}
          />
          {registrationData.phone && !registrationData.phone.startsWith("+") && (
            <span style={{ fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>
              Must start with + country code (e.g. +61 for Australia, +1 for US)
            </span>
          )}
        </div>
      </div>

      {/* DOB — required for Individual & Sole Trader, optional for others */}
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            Date of Birth{" "}
            {isIndividual || entitySubType === "sole-trader" ? (
              "*"
            ) : (
              <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 500, fontStyle: "italic" }}>
                (optional)
              </span>
            )}
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              value={dobDay}
              onChange={(e) =>
                handleRegistrationFieldChange(
                  "dateOfBirth",
                  buildDate(dobYear, dobMonth, e.target.value),
                )
              }
              required
              className="filter-select"
              style={{ flex: 1, padding: "10px 8px", fontSize: 14 }}
            >
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d.toString()}>
                  {d}
                </option>
              ))}
            </select>
            <select
              value={dobMonth}
              onChange={(e) =>
                handleRegistrationFieldChange(
                  "dateOfBirth",
                  buildDate(dobYear, e.target.value, dobDay),
                )
              }
              required
              className="filter-select"
              style={{ flex: 1.4, padding: "10px 8px", fontSize: 14 }}
            >
              <option value="">Month</option>
              {[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ].map((m, i) => (
                <option key={i + 1} value={(i + 1).toString()}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={dobYear === "0000" ? "" : dobYear}
              onChange={(e) =>
                handleRegistrationFieldChange(
                  "dateOfBirth",
                  buildDate(e.target.value, dobMonth, dobDay),
                )
              }
              required
              className="filter-select"
              style={{ flex: 1, padding: "10px 8px", fontSize: 14 }}
            >
              <option value="">Year</option>
              {Array.from(
                { length: 100 },
                (_, i) => new Date().getFullYear() - i,
              ).map((y) => (
                <option key={y} value={y.toString()}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ABN/ACN — read-only card for business entities */}
      {!isIndividual && formAssessment?.entityAbn && (
        <div
          style={{
            padding: "12px 16px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 8,
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>✅</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: "#111" }}>
              {formAssessment.clientName}
            </span>
            <span style={{ fontSize: 12, color: "#555" }}>
              {registrationData.legalEntityCodeType}:{" "}
              {registrationData.legalEntityCodeType === "ABN"
                ? formatABN(formAssessment.entityAbn)
                : registrationData.legalEntityCodeType === "ACN"
                  ? formatACN(formAssessment.entityAbn)
                  : formAssessment.entityAbn}
            </span>
            <span style={{ fontSize: 11, color: "#16a34a" }}>
              Verified during risk assessment
            </span>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          marginTop: 24,
        }}
      >
        <button
          type="button"
          className="btn-cancel"
          onClick={() => setWizardStep(1)}
        >
          ← Back
        </button>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            className="btn-cancel"
            onClick={handleCloseForm}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-submit"
            onClick={() => setWizardStep(3)}
            disabled={!canProceed}
            style={{ opacity: canProceed ? 1 : 0.5 }}
          >
            Review →
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardStepTwo;
